// pages/customer/products.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { productsAPI } from '../../api/products';
import type { Product } from '../../types';
import ProductCard from '../../components/customer/ProductCard';
import { Filter, Grid, List, X, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { useNavigate } from 'react-router-dom';

const Products: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  // const [category, setCategory] = useState(searchParams.get('category') || '');
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({});
  const page = searchParams.get('page') || '1';
  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const [inStockOnly, setInStockOnly] = useState(searchParams.get('in_stock') === 'true');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const navigate = useNavigate();

  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Categories (would come from API in real app)
  const categories = [
    { id: 'electronics', name: 'Electronics', count: 24 },
    { id: 'computers', name: 'Computers & Laptops', count: 18 },
    { id: 'phones', name: 'Phones & Tablets', count: 15 },
    { id: 'accessories', name: 'Accessories', count: 32 },
    { id: 'components', name: 'PC Components', count: 27 },
  ];
  
  useEffect(() => {
    // Fetch categories from API and create mapping
    // { 'electronics': 'Electronics', 'mobile-accessories': 'Mobile Accessories' }
  }, []);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await productsAPI.getProducts({
        page: currentPage,
        page_size: 12,
        search: debouncedSearch || undefined,
        category: category || undefined,
        in_stock_only: inStockOnly,
      });

      if (response.data) {
        // Check if response.data is an array
        let productsArray: Product[] = [];
        let totalCount = 0;

        if (Array.isArray(response.data)) {
          // If it's already an array of products
          productsArray = response.data;
          totalCount = response.data.length;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          // If it's a paginated response { results: Product[], count: number }
          productsArray = response.data.results;
          totalCount = response.data.count || response.data.total || productsArray.length;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // If it's wrapped in data property
          productsArray = response.data.data;
          totalCount = response.data.count || response.data.total || productsArray.length;
        } else {
          // Fallback: try to extract array from response
          console.warn('Unexpected API response format:', response.data);
          productsArray = Object.values(response.data).find(val =>
            Array.isArray(val) && val.length > 0 && val[0].id !== undefined
          ) as Product[] || [];
          totalCount = productsArray.length;
        }

        setProducts(productsArray);

        // Calculate actual total pages
        const actualTotalPages = Math.ceil(totalCount / 12);
        setTotalPages(actualTotalPages || 1);

        // Reset to page 1 if current page exceeds total pages
        if (currentPage > actualTotalPages && actualTotalPages > 0) {
          setCurrentPage(1);
        }
      } else if (response.error) {
        setError(response.error);
      }
    } catch (err) {
      setError('Failed to load products. Please try again.');
      console.error('Error fetching products:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch, category, inStockOnly]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, category, search, page]);

  // Update URL params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.q = debouncedSearch;
    if (category) params.category = category;
    if (inStockOnly) params.in_stock = 'true';

    setSearchParams(params, { replace: true });
  }, [debouncedSearch, category, inStockOnly, setSearchParams]);

  const clearFilters = () => {
    navigate('/products'); // Navigate without any params
    setInStockOnly(false);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  const activeFiltersCount = (searchTerm ? 1 : 0) + (category ? 1 : 0) + (inStockOnly ? 1 : 0);
  const handleCategorySelect = (categoryId: string) => {
    navigate(`/products?category=${categoryId}`);
  };

  // Skeleton Loaders
  const SkeletonProductGrid = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="aspect-square bg-gray-200 animate-pulse" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-gray-200 rounded animate-pulse" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
            <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );

  const SkeletonProductList = () => (
    <div className="space-y-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-48 h-48 bg-gray-200 animate-pulse" />
            <div className="flex-1 p-4 md:p-6 space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
              <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3" />
              <div className="flex justify-between items-center">
                <div className="h-7 bg-gray-200 rounded animate-pulse w-24" />
                <div className="h-10 bg-gray-200 rounded-lg animate-pulse w-28" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-4 sm:py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-3">Categories</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate('/products')}
                    className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${!category ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    All Categories
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.id)}
                      className={`block w-full text-left px-3 py-2 rounded-lg text-sm flex justify-between items-center ${category === cat.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <span>{cat.name}</span>
                      <span className="text-gray-500 text-xs">{cat.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Stock Filter */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-3">Availability</h3>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700">In stock only</span>
                </label>
              </div>

              {/* Price Filter (Optional) */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-3">Price Range</h3>
                <div className="space-y-2">
                  {['Under $50', '$50 - $200', '$200 - $500', 'Over $500'].map((range) => (
                    <button
                      key={range}
                      className="block w-full text-left px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>

              {/* Active Filters */}
              {activeFiltersCount > 0 && (
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-3">Active Filters</h3>
                  <div className="flex flex-wrap gap-2">
                    {searchTerm && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Search: {searchTerm}
                        <button
                          onClick={() => setSearchTerm('')}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {category && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {categories.find(c => c.id === category)?.name}
                        <button
                          onClick={() => navigate('/products')}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {inStockOnly && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        In stock only
                        <button
                          onClick={() => setInStockOnly(false)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            {/* Header with breadcrumb */}
            <div className="mb-6">
              <nav className="flex items-center text-sm text-gray-500 mb-3">
                <span>Home</span>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span>Products</span>
                {category && (
                  <>
                    <ChevronRight className="h-4 w-4 mx-2" />
                    <span className="text-gray-900 font-medium">
                      {categories.find(c => c.id === category)?.name}
                    </span>
                  </>
                )}
              </nav>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {category
                      ? categoryNames[category] || category
                      : 'All Products'
                    }
                  </h1>
                  <p className="mt-1 text-gray-600">
                    {products.length > 0
                      ? `${products.length} product${products.length !== 1 ? 's' : ''} found`
                      : 'Discover quality tech products'
                    }
                  </p>
                </div>

                {/* View toggle and filter button */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                      aria-label="Grid view"
                    >
                      <Grid className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                      aria-label="List view"
                    >
                      <List className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Mobile filter button */}
                  <button
                    onClick={() => setShowMobileFilters(true)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Filter className="h-4 w-4" />
                    Filters
                    {activeFiltersCount > 0 && (
                      <span className="bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile filter chips */}
            {activeFiltersCount > 0 && (
              <div className="lg:hidden mb-4">
                <div className="flex flex-wrap gap-2">
                  {searchTerm && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-800">
                      Search: "{searchTerm}"
                      <button
                        onClick={() => setSearchTerm('')}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {category && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-800">
                      {categories.find(c => c.id === category)?.name}
                      <button
                        onClick={() => navigate('/products')}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  {inStockOnly && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-800">
                      In stock only
                      <button
                        onClick={() => setInStockOnly(false)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                  <button
                    onClick={clearFilters}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              viewMode === 'grid' ? <SkeletonProductGrid /> : <SkeletonProductList />
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <div className="text-red-600 font-medium mb-2">Unable to load products</div>
                <p className="text-red-500 text-sm mb-4">{error}</p>
                <button
                  onClick={fetchProducts}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Products */}
            {!isLoading && !error && (
              <>
                {products.length > 0 ? (
                  <>
                    <div className={viewMode === 'grid'
                      ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6'
                      : 'space-y-4'
                    }>
                      {products.map((product) => (
                        <ProductCard
                          key={product.id}
                          product={product}
                          viewMode={viewMode}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && products.length > 0 && (
                      <div className="mt-8 flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          Page {currentPage} of {totalPages}
                        </div>
                        <nav className="flex items-center gap-1">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>

                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1)
                            .map(pageNum => (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`w-10 h-10 rounded-lg text-sm font-medium ${currentPage === pageNum
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                              >
                                {pageNum}
                              </button>
                            ))}

                          <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </nav>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Search className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
                    <p className="mt-2 text-gray-500 max-w-sm mx-auto">
                      {currentPage > 1
                        ? 'No more products on this page'
                        : searchTerm || category || inStockOnly
                          ? 'Try adjusting your filters or search term'
                          : 'No products are currently available'
                      }
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                      {currentPage > 1 && (
                        <button
                          onClick={() => setCurrentPage(1)}
                          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                        >
                          Go to first page
                        </button>
                      )}
                      {(searchTerm || category || inStockOnly) && (
                        <button
                          onClick={clearFilters}
                          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileFilters(false)} />
          <div className="absolute inset-y-0 right-0 w-full max-w-sm bg-white shadow-xl">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Categories</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => navigate('/products')}
                      className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm ${!category ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                    >
                      All Categories
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat.id)}
                        className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm ${category === cat.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inStockOnly}
                      onChange={(e) => setInStockOnly(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm text-gray-700">In stock only</span>
                  </label>
                </div>
              </div>
              {products.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <Search className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
                  <p className="mt-2 text-gray-500 max-w-sm mx-auto">
                    {currentPage > 1
                      ? `No products on page ${currentPage}.`
                      : searchTerm || category || inStockOnly
                        ? 'Try adjusting your filters or search term'
                        : 'No products are currently available'
                    }
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                    {currentPage > 1 && (
                      <button
                        onClick={() => setCurrentPage(1)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                      >
                        Go to first page
                      </button>
                    )}
                    {(searchTerm || category || inStockOnly) && (
                      <button
                        onClick={clearFilters}
                        className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                </div>
              )}
              {/* Footer */}
              <div className="border-t p-4">
                <div className="flex gap-3">
                  <button
                    onClick={clearFilters}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg"
                  >
                    Clear all
                  </button>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;